import honeybadger from "@honeybadger-io/js";
import { UnauthenticatedError } from "@sp-crm/core";
import axios from "axios";
import { GatewayTimeoutError } from "backend/fetcher";
import {
    captureExceptionToSink,
    mergeExceptionContext,
    toExceptionProperties,
} from "./exception-sink";
import { getServerInfo } from "./server";
import { configureExceptionCapture } from "./user-events";
import { buildVersion } from "./version";

let instance: honeybadger;

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- eslintintroduction
const delayedContext: any[] = [];

interface HoneybadgerFrame {
    file?: string;
    function?: string;
    method?: string;
    number?: number;
}

interface HoneybadgerNotice {
    name?: string;
    message?: string;
    stack?: string;
    backtrace?: HoneybadgerFrame[];
}

const THIRD_PARTY_DOMAINS = ["helpscout.net"];

const isNonAppFrame = (frame: HoneybadgerFrame): boolean =>
    frame?.file === "[native code]" ||
    frame?.file?.includes("<unknown>") ||
    !!frame?.file?.match(/<anonymous.*>/);

const isEntirelyFromThirdPartyDomains = (notice: HoneybadgerNotice): boolean => {
    if (!notice?.backtrace?.length) {
        return false;
    }

    const hasThirdPartyFrame = notice.backtrace.some(frame =>
        THIRD_PARTY_DOMAINS.some(domain => frame?.file?.includes(domain)),
    );
    if (!hasThirdPartyFrame) {
        return false;
    }

    return notice.backtrace.every(
        frame =>
            THIRD_PARTY_DOMAINS.some(domain => frame?.file?.includes(domain)) ||
            isNonAppFrame(frame),
    );
};

const isThirdPartyChunkLoadError = (notice: HoneybadgerNotice): boolean => {
    if (notice?.name !== "ChunkLoadError") {
        return false;
    }
    return THIRD_PARTY_DOMAINS.some(domain => notice.message?.includes(domain));
};

const isReactPdfRaceCondition = (notice: HoneybadgerNotice): boolean => {
    if (!notice?.message?.includes("getComputedStyle")) {
        return false;
    }
    if (notice?.stack?.includes("pdf")) {
        return true;
    }
    return notice?.backtrace?.some(frame => frame?.file?.includes("pdf")) ?? false;
};

export const isMaplibreInternalError = (notice: HoneybadgerNotice): boolean => {
    if (notice?.stack?.includes("maplibre")) {
        return true;
    }
    if (notice?.backtrace?.some(frame => frame?.file?.includes("maplibre"))) {
        return true;
    }
    const maplibreMessagePatterns = [
        "is not iterable",
        "transient activation",
        "doesn't match current length",
    ];
    const isMaplibreMessage = maplibreMessagePatterns.some(pattern =>
        notice?.message?.includes(pattern),
    );
    const isMapChunkOrNativeFrame = (frame: HoneybadgerFrame) =>
        frame?.file?.includes("/map-") || frame?.file === "[native code]";
    const allFramesAreMapChunk =
        notice?.backtrace?.length > 0 && notice.backtrace.every(isMapChunkOrNativeFrame);
    return isMaplibreMessage && allFramesAreMapChunk;
};

export const isExternalError = (notice: HoneybadgerNotice): boolean => {
    if (notice?.stack && /chrome-extension:\/\//.test(notice.stack)) {
        return true;
    }

    if (!notice?.backtrace?.length) {
        return false;
    }

    const honeybadgerFiles = new Set(
        notice.backtrace
            .filter(frame => frame?.method?.includes("___hb"))
            .map(frame => frame?.file)
            .filter(Boolean),
    );

    const allFramesAreUnknown = notice.backtrace.every(
        (frame: HoneybadgerFrame) =>
            frame?.file?.includes("chrome-extension://") ||
            frame?.file?.includes("<unknown>") ||
            frame?.file?.match(/<anonymous.*>/) ||
            frame?.file === "[native code]" ||
            honeybadgerFiles.has(frame?.file),
    );

    if (allFramesAreUnknown) {
        return true;
    }

    return isEntirelyFromThirdPartyDomains(notice);
};

export const shouldFilterDynamicImportError = (notice: HoneybadgerNotice): boolean => {
    if (!notice?.message) {
        return false;
    }

    if (notice.message.includes("Failed to fetch dynamically imported module")) {
        return true;
    }

    const isPdfWorkerLoadError =
        notice.message.includes("error loading dynamically imported module") &&
        (notice.message.includes("pdf.worker") || notice.message.includes("pdfjs-dist"));

    return isPdfWorkerLoadError;
};

export const isFramerMotionScrollError = (notice: HoneybadgerNotice): boolean => {
    if (!notice?.message || !notice?.stack) {
        return false;
    }

    const isScrollLeftNullError =
        notice.message.includes("Cannot read properties of null") &&
        notice.message.includes("scrollLeft");

    if (!isScrollLeftNullError) {
        return false;
    }

    return notice.stack.includes("measureScroll");
};

export const isEmptyObjectRejectionWithNoUsefulTrace = (
    notice: HoneybadgerNotice,
): boolean => {
    if (
        notice?.message !== "UnhandledPromiseRejectionWarning: {}" &&
        notice?.message !== "UnhandledPromiseRejectionWarning: Unspecified reason" &&
        notice?.message !== "UnhandledPromiseRejectionWarning: undefined"
    ) {
        return false;
    }

    if (!notice?.backtrace?.length) {
        return true;
    }

    return notice.backtrace.every((frame, _, arr) => {
        const methodName = frame?.method || frame?.function;
        const isMinifiedMethod =
            !methodName || methodName === "<unknown>" || methodName.length <= 2;
        const isSameLineAsOthers = arr.every(f => f.number === frame.number);
        return isMinifiedMethod && isSameLineAsOthers;
    });
};

const TWILIO_AUDIO_OUTPUT_FRAME_METHODS = [
    "_setSinkIds",
    "_updateSinkIds",
    "_removeLostOutput",
];

export const isTwilioAudioOutputSelectionError = (notice: HoneybadgerNotice): boolean => {
    if (notice?.name !== "NotSupportedError") {
        return false;
    }

    if (!notice?.message?.includes("Audio output selection is not supported")) {
        return false;
    }

    if (
        TWILIO_AUDIO_OUTPUT_FRAME_METHODS.some(method => notice?.stack?.includes(method))
    ) {
        return true;
    }

    return (
        notice?.backtrace?.some(frame =>
            TWILIO_AUDIO_OUTPUT_FRAME_METHODS.some(
                method =>
                    frame?.method?.includes(method) || frame?.function?.includes(method),
            ),
        ) ?? false
    );
};

const NOTABLE_ABORT_ERROR_MESSAGES = ["signal timed out"];

export const isBenignAbortError = (notice: HoneybadgerNotice): boolean => {
    if (notice?.name !== "AbortError") {
        return false;
    }

    return !NOTABLE_ABORT_ERROR_MESSAGES.some(msg => notice?.message?.includes(msg));
};

const WEBKIT_MASKED_URL_SCHEME = "webkit-masked-url://";

const isWebkitMaskedFrame = (frame: HoneybadgerFrame): boolean =>
    frame?.file?.startsWith(WEBKIT_MASKED_URL_SCHEME) ?? false;

const isEntirelyFromWebkitMaskedScript = (notice: HoneybadgerNotice): boolean => {
    if (!notice?.backtrace?.length) {
        return false;
    }

    if (!notice.backtrace.some(isWebkitMaskedFrame)) {
        return false;
    }

    return notice.backtrace.every(
        frame => isWebkitMaskedFrame(frame) || isNonAppFrame(frame),
    );
};

export const isWebkitMaskedUrlError = (notice: HoneybadgerNotice): boolean => {
    if (isEntirelyFromWebkitMaskedScript(notice)) {
        return true;
    }

    if (!notice?.stack?.includes(WEBKIT_MASKED_URL_SCHEME)) {
        return false;
    }

    const unspecifiedReasonMessages = [
        "UnhandledPromiseRejectionWarning: Unspecified reason",
        "UnhandledPromiseRejectionWarning: {}",
        "UnhandledPromiseRejectionWarning: undefined",
    ];

    return unspecifiedReasonMessages.some(msg => notice?.message === msg);
};

export const isMapboxGlError = (notice: HoneybadgerNotice): boolean => {
    if (!notice?.message) {
        return false;
    }

    const mapboxPatterns = [
        "opacityVertexArray.length",
        "layoutVertexArray.length",
        "Could not compile fragment shader",
        "Could not compile vertex shader",
        "Program failed to link",
    ];

    return mapboxPatterns.some(pattern => notice.message?.includes(pattern));
};

export const FAILED_FETCH_MESSAGES = [
    "Failed to fetch",
    "Load failed",
    "network error",
    "NetworkError when attempting to fetch resource.",
];

export const isFailedToFetchWithNoUsefulTrace = (notice: HoneybadgerNotice): boolean => {
    if (!FAILED_FETCH_MESSAGES.includes(notice?.message)) {
        return false;
    }

    if (!notice?.backtrace?.length) {
        return true;
    }

    const isHoneybadgerInternal = notice.backtrace.some(
        frame =>
            frame?.method === "Object.postUserData" ||
            frame?.method === "postUserData" ||
            frame?.method?.includes("___hb"),
    );
    if (isHoneybadgerInternal) {
        return true;
    }

    const allFramesLackUsefulInfo = notice.backtrace.every(frame => {
        const method = frame?.method || frame?.function;
        return !method || method === "<unknown>";
    });
    if (allFramesLackUsefulInfo) {
        return true;
    }

    return false;
};

const isFromReactDomOrVendorBundle = (notice: HoneybadgerNotice): boolean => {
    if (
        notice.stack?.includes("react-dom") ||
        notice.stack?.includes("react-dom.production")
    ) {
        return true;
    }

    if (notice?.backtrace?.some(frame => frame?.file?.includes("/vendor-"))) {
        return true;
    }

    return false;
};

export const isDomManipulationErrorFromExtension = (
    notice: HoneybadgerNotice,
): boolean => {
    if (!notice?.message) {
        return false;
    }

    const domManipulationPatterns = [
        "Failed to execute 'removeChild' on 'Node'",
        "Failed to execute 'insertBefore' on 'Node'",
        "Failed to execute 'appendChild' on 'Node'",
        "The node to be removed is not a child of this node",
        "The object can not be found here.",
    ];

    if (!isFromReactDomOrVendorBundle(notice)) {
        return false;
    }

    return domManipulationPatterns.some(pattern => notice.message?.includes(pattern));
};

export const isCircularJsonSerializationErrorFromExtension = (
    notice: HoneybadgerNotice,
): boolean => {
    if (!notice?.message?.includes("Converting circular structure to JSON")) {
        return false;
    }

    return (
        notice.message.includes("__reactFiber$") ||
        notice.message.includes("__reactProps$")
    );
};

export const isLikelyCefSharpBotError = (notice: HoneybadgerNotice): boolean => {
    return !!notice?.message?.match(/Object Not Found Matching Id:\d+, MethodName:\w+/);
};

const INJECTED_BROWSER_GLOBALS = ["__firefox__", "__gCrWeb"];

export const isInjectedBrowserGlobalError = (notice: HoneybadgerNotice): boolean => {
    if (notice?.name !== "ReferenceError") {
        return false;
    }
    return INJECTED_BROWSER_GLOBALS.some(
        identifier =>
            notice?.message?.includes(`Can't find variable: ${identifier}`) ||
            notice?.message?.includes(`${identifier} is not defined`),
    );
};

export const isBrowserExtensionRuntimeMessagingError = (
    notice: HoneybadgerNotice,
): boolean => {
    return !!notice?.message?.startsWith("Invalid call to runtime.");
};

const GLOBAL_SCOPE_METHOD = "global code";

const isJavascriptAsset = (file: string): boolean => {
    const [path] = file.split(/[?#]/);
    return path.endsWith(".js");
};

export const isInlineGlobalScopeError = (notice: HoneybadgerNotice): boolean => {
    if (notice?.backtrace?.length !== 1) {
        return false;
    }

    const [frame] = notice.backtrace;
    if (frame?.method !== GLOBAL_SCOPE_METHOD || !frame.file) {
        return false;
    }

    return !isJavascriptAsset(frame.file);
};

export const isPosthogSessionRecordingError = (notice: HoneybadgerNotice): boolean => {
    if (
        notice?.message?.includes(
            "Called on script loaded before session recording is available",
        )
    ) {
        return true;
    }

    const frames = notice?.backtrace;
    if (Array.isArray(frames) && frames.length > 0) {
        const allPosthog = frames.every(
            frame =>
                frame?.file?.includes("posthog.com/") || frame?.file === "<anonymous>",
        );
        if (allPosthog) {
            return true;
        }
    }

    return false;
};

export const isGatewayTimeoutError = (error: unknown): boolean => {
    return error instanceof GatewayTimeoutError;
};

export const isUnauthenticatedError = (error: unknown): boolean =>
    axios.isAxiosError(error) && error.response?.status === 401;

export const isUnauthorizedError = (error: unknown): boolean =>
    axios.isAxiosError(error) && error.response?.status === 403;

export const isTooManyRequestsError = (error: unknown): boolean =>
    axios.isAxiosError(error) && error.response?.status === 429;

export const isReportableToPosthog = (error: unknown): boolean =>
    !shouldSkipReporting(error) &&
    !isUnauthenticatedError(error) &&
    !isUnauthorizedError(error) &&
    !(error instanceof UnauthenticatedError);

const hasSkipReportingFlag = (value: unknown): boolean =>
    !!value &&
    typeof value === "object" &&
    !!(value as Record<string, unknown>).skipReporting;

export const shouldSkipReporting = (error: unknown): boolean => {
    if (!error || typeof error !== "object") {
        return false;
    }

    const record = error as Record<string, unknown>;
    return (
        hasSkipReportingFlag(record) ||
        hasSkipReportingFlag(record.cause) ||
        hasSkipReportingFlag(record.data)
    );
};

export const safeStringify = (value: unknown): string | undefined => {
    try {
        return JSON.stringify(value);
    } catch {
        return undefined;
    }
};

interface ErrorLike {
    name: string;
    message: string;
    stack?: string;
}

const isErrorLike = (value: unknown): value is ErrorLike => {
    if (!value || typeof value !== "object") {
        return false;
    }
    const record = value as Record<string, unknown>;
    return (
        typeof record.name === "string" &&
        typeof record.message === "string" &&
        (record.stack === undefined || typeof record.stack === "string")
    );
};

const describeUnknownReason = (reason: unknown): string => {
    if (typeof reason === "string") {
        return reason;
    }
    const stringified = safeStringify(reason);
    return stringified && stringified !== "{}" ? stringified : "Unspecified reason";
};

export const noticeForRejectionReason = (reason: unknown): Error | ErrorLike => {
    if (reason instanceof Error) {
        return reason;
    }
    if (isErrorLike(reason)) {
        return { name: reason.name, message: reason.message, stack: reason.stack };
    }
    return {
        name: "UnhandledPromiseRejection",
        message: `UnhandledPromiseRejectionWarning: ${describeUnknownReason(reason)}`,
    };
};

export const toReportableError = (value: unknown): Error => {
    if (value instanceof Error) {
        return value;
    }
    if (isErrorLike(value)) {
        const error = new Error(value.message);
        error.name = value.name;
        if (value.stack) {
            error.stack = value.stack;
        }
        return error;
    }
    return new Error(describeUnknownReason(value));
};

const setupUnhandledRejectionHandler = () => {
    window.addEventListener("unhandledrejection", (event: PromiseRejectionEvent) => {
        const reason = event.reason;

        if (isReportableToPosthog(reason)) {
            captureExceptionToSink(reason, { unhandled_rejection: true });
        }

        if (!instance) {
            return;
        }

        if (shouldSkipReporting(reason)) {
            return;
        }

        if (isUnauthorizedError(reason)) {
            return;
        }

        const notice = noticeForRejectionReason(reason);
        instance.addBreadcrumb(`window.onunhandledrejection: ${notice.name}`, {
            category: "error",
            metadata: {
                name: notice.name,
                message: notice.message,
                stack: notice.stack || `${notice.message}\n    at ? (unknown:0)`,
            },
        });
        instance.notify(notice);
    });
};

export const shouldCapturePosthogExceptions = (environment: string): boolean =>
    environment !== "development";

export const configureErrorTracking = async () => {
    const serverInfo = await getServerInfo();
    mergeExceptionContext({ serverVersion: serverInfo.build });
    if (shouldCapturePosthogExceptions(serverInfo.environment)) {
        configureExceptionCapture(serverInfo.environment);
    }
    if (!instance) {
        honeybadger.beforeNotify((notice): boolean | void => {
            if (
                notice?.name === "ClientGraphqlNetworkError" ||
                notice?.name === "ClientHttpNetworkError" ||
                notice?.name === "UnauthenticatedError"
            ) {
                return false;
            }

            if (notice?.message?.includes("ResizeObserver")) {
                return false;
            }

            if (isFramerMotionScrollError(notice)) {
                return false;
            }

            if (shouldFilterDynamicImportError(notice)) {
                return false;
            }

            if (
                notice?.message?.includes("Unexpected keyword 'import'") &&
                (notice?.backtrace?.some(frame => frame?.file?.includes("<unknown>")) ||
                    notice?.stack?.includes("blob:"))
            ) {
                return false;
            }

            if (isMapboxGlError(notice)) {
                return false;
            }

            if (isMaplibreInternalError(notice)) {
                return false;
            }

            if (isFailedToFetchWithNoUsefulTrace(notice)) {
                return false;
            }

            if (isLikelyCefSharpBotError(notice)) {
                return false;
            }

            // Filter MetaMask browser extension errors
            if (notice?.message?.includes("Failed to connect to MetaMask")) {
                return false;
            }

            if (isBrowserExtensionRuntimeMessagingError(notice)) {
                return false;
            }

            if (isWebkitMaskedUrlError(notice)) {
                return false;
            }

            if (isExternalError(notice)) {
                return false;
            }

            if (isInjectedBrowserGlobalError(notice)) {
                return false;
            }

            if (isInlineGlobalScopeError(notice)) {
                return false;
            }

            if (isThirdPartyChunkLoadError(notice)) {
                return false;
            }

            if (isReactPdfRaceCondition(notice)) {
                return false;
            }

            if (isEmptyObjectRejectionWithNoUsefulTrace(notice)) {
                return false;
            }

            if (isDomManipulationErrorFromExtension(notice)) {
                return false;
            }

            if (isCircularJsonSerializationErrorFromExtension(notice)) {
                return false;
            }

            if (isBenignAbortError(notice)) {
                return false;
            }

            if (isPosthogSessionRecordingError(notice)) {
                return false;
            }

            if (isTwilioAudioOutputSelectionError(notice)) {
                return false;
            }
        });
        instance = honeybadger.configure({
            apiKey: "6502fcd2",
            environment: serverInfo.environment,
            revision: buildVersion,
            enableUnhandledRejection: false,
        });

        instance.setContext({
            serverVersion: serverInfo.build,
        });

        setupUnhandledRejectionHandler();
    }

    delayedContext.forEach(x => x(instance));
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- eslintintroduction
(window as any).customReport = () => {
    try {
        throw "this is a test!";
    } catch (e) {
        honeybadger.notify(e instanceof Error ? e : new Error(String(e)));
    }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- eslintintroduction
(window as any).customReportOnInstance = () => {
    try {
        throw "this is a test!";
    } catch (e) {
        instance.notify(e instanceof Error ? e : new Error(String(e)));
    }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- eslintintroduction
(window as any).testFingerprint = () => {
    reportError(new Error("Test error with fingerprint"), {
        fingerprint: "test-fingerprint-123",
    });
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- eslintintroduction
(window as any).testParams = () => {
    reportError(new Error("Test error with params"), {
        params: { userId: "test-user-456", action: "button-click", count: 42 },
    });
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- eslintintroduction
(window as any).testContext = () => {
    reportError(new Error("Test error with context"), {
        context: { feature: "error-tracking-test", environment: "manual-test" },
    });
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- eslintintroduction
(window as any).testAllOptions = () => {
    reportError(new Error("Test error with all options"), {
        fingerprint: "combined-test-fingerprint",
        params: { source: "manual-test", timestamp: Date.now() },
        context: { testType: "full-options-test" },
    });
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- eslintintroduction
(window as any).testDuplicateFingerprint = () => {
    reportError(new Error("First error - should dedupe"), {
        fingerprint: "dedupe-test-fingerprint",
    });
    reportError(new Error("Second error - same fingerprint"), {
        fingerprint: "dedupe-test-fingerprint",
    });
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- eslintintroduction
export const setErrorTrackingContext = (context: any) => {
    mergeExceptionContext(context);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- eslintintroduction
    const callback = (i: any) => i.setContext(context);
    if (instance?.setContext) {
        callback(instance);
    } else {
        delayedContext.push(callback);
    }
};

const errorClassName = (error: unknown): string => {
    if (error && typeof error === "object") {
        const name = (error as { name?: unknown }).name;
        if (typeof name === "string" && name.length > 0) {
            return name;
        }
    }
    return "UnknownError";
};

export const makeErrorFingerprint = (
    scope: "query" | "mutation" | "trpc",
    key: string,
    error: unknown,
): string => `${scope}:${key}:${errorClassName(error)}`;

export interface ReportErrorOptions {
    fingerprint?: string;
    params?: Record<string, unknown>;
    context?: Record<string, unknown>;
}

const toPosthogProperties = (options: ReportErrorOptions): Record<string, unknown> => ({
    ...toExceptionProperties(options.context ?? {}),
    ...(options.params ?? {}),
    ...(options.fingerprint ? { hb_fingerprint: options.fingerprint } : {}),
});

export const reportError = (e: Error, options: ReportErrorOptions = {}) => {
    captureExceptionToSink(e, toPosthogProperties(options));
    if (!instance) return;
    const notifyOptions: Record<string, unknown> = {};
    if (options.fingerprint) {
        notifyOptions.fingerprint = options.fingerprint;
    }
    if (options.params) {
        notifyOptions.params = options.params;
    }
    if (options.context) {
        notifyOptions.context = options.context;
    }
    instance.notify(e, Object.keys(notifyOptions).length > 0 ? notifyOptions : undefined);
};
